/**
 * salesActivities.service — R7a Sales Activities (2026-05-18).
 *
 * Thin RPC wrappers for phone-sale CRUD and the three R7a reports.
 * All RPCs are SECURITY DEFINER on the DB side; create_phone_sale and
 * cancel_phone_sale enforce caller-vs-rep and creator-vs-admin
 * authorization respectively. Report RPCs enforce admin/manager role
 * server-side.
 *
 * Shape contracts mirror the RPC RETURNS TABLE 1:1 so report rows can
 * pass straight into ExportButton's mapRows without a translation
 * layer (mirrors reportsSuite.service.ts pattern).
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

// ---------------- create_phone_sale ----------------

export interface CreatePhoneSaleInput {
  repPositionId: UUID;
  contactId: UUID;
  organizationId?: UUID | null;
  eventId?: UUID | null;
  /**
   * Caller-provided UUID for idempotency. Re-submitting with the same
   * (created_by, clientId) returns the existing row id. Recommended:
   * crypto.randomUUID() once at form-mount, reuse on retry. If omitted
   * the DB generates a fresh UUID, which means re-submits will
   * duplicate.
   */
  clientId?: UUID;
  notes?: string;
  payload?: unknown;
}

/**
 * Create a phone-sale activity. Returns the row id of the new (or
 * idempotently-matched) row. Throws on validation/auth errors:
 *
 *   - "authentication required" — auth.uid() is null
 *   - "contact_id is required for phone_sale (R7a-D2)"
 *   - "contact_id <uuid> does not exist"
 *   - "rep_position_id <uuid> is not an active sales_position"
 *   - "not authorized to credit sales_position_id <uuid>" — caller is
 *     neither admin/manager nor assigned to the rep position
 */
export async function createPhoneSale(
  input: CreatePhoneSaleInput,
): Promise<UUID> {
  const { data, error } = await supabase.rpc("create_phone_sale", {
    p_rep_position_id: input.repPositionId,
    p_contact_id: input.contactId,
    p_organization_id: input.organizationId ?? null,
    p_event_id: input.eventId ?? null,
    p_client_id: input.clientId ?? null,
    p_notes: input.notes ?? null,
    p_payload: input.payload ?? null,
  });
  if (error) {
    throw error;
  }
  return data as UUID;
}

// ---------------- record_offline_field_sale ----------------

export interface RecordOfflineFieldSaleInput {
  repPositionId: UUID;
  /** New-signup contact details captured offline. Email is REQUIRED (the resolver keys on it). */
  signup: {
    firstName?: string | null;
    lastName?: string | null;
    email: string;
    phone?: string | null;
    zip?: string | null;
  };
  organizationId?: UUID | null;
  eventId?: UUID | null;
  /** Idempotency key — generated ONCE at capture, reused on every sync retry. */
  clientId: UUID;
  notes?: string | null;
  payload?: unknown;
}

/**
 * Sync one offline-captured field sale: server-side resolves-or-creates the
 * contact from the signup email (race-safe) and records the phone sale,
 * atomically + idempotently by (created_by, clientId). Returns the sale id.
 * Throws on auth/validation/business errors (PostgrestError with a `.code`,
 * e.g. P0001 for RAISE) — callers should treat those as definitive (no retry).
 */
export async function recordOfflineFieldSale(
  input: RecordOfflineFieldSaleInput,
): Promise<UUID> {
  const { data, error } = await supabase.rpc("record_offline_field_sale", {
    p_rep_position_id: input.repPositionId,
    p_email: input.signup.email,
    p_first_name: input.signup.firstName ?? null,
    p_last_name: input.signup.lastName ?? null,
    p_phone: input.signup.phone ?? null,
    p_postal_code: input.signup.zip ?? null,
    p_organization_id: input.organizationId ?? null,
    p_event_id: input.eventId ?? null,
    p_client_id: input.clientId,
    p_notes: input.notes ?? null,
    p_payload: input.payload ?? null,
  });
  if (error) {
    throw error;
  }
  return data as UUID;
}

// ---------------- cancel_phone_sale ----------------

/**
 * Soft-cancel a phone sale by setting status='cancelled'. Authorized
 * for superadmin/admin/manager OR the row's creator. Returns true on
 * success. Throws:
 *
 *   - "authentication required"
 *   - "sales_activity <uuid> not found"
 *   - "cancel_phone_sale only operates on phone_sale rows" — caller
 *     tried to cancel a non-phone_sale (future offline_signup, etc.)
 *   - "not authorized to cancel this phone sale"
 */
export async function cancelPhoneSale(activityId: UUID): Promise<boolean> {
  const { data, error } = await supabase.rpc("cancel_phone_sale", {
    p_activity_id: activityId,
  });
  if (error) {
    throw error;
  }
  return Boolean(data);
}

// ---------------- Shared filter shape (matches RPC params) ----------------

export interface SalesActivityReportFilter {
  orgIds?: UUID[];
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  repPositionId?: UUID;
  page: number;
  limit: number;
}

// ---------------- get_phone_sales_report ----------------

export interface PhoneSalesReportRow {
  activity_id: UUID;
  rep_position_id: UUID;
  contact_id: UUID | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  member_number: string | null;
  organization_id: UUID | null;
  event_id: UUID | null;
  notes: string | null;
  status: string;
  created_at: string;
  total_count: number;
}

/**
 * Row-level phone-sales report. PII-gated: throws "Insufficient role"
 * for roles outside superadmin/admin/manager. Cancelled rows are
 * excluded server-side.
 */
export async function getPhoneSalesReport(
  filter: SalesActivityReportFilter,
): Promise<{ rows: PhoneSalesReportRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_phone_sales_report", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_rep_position_id: filter.repPositionId || null,
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as PhoneSalesReportRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

/**
 * Fetch a single phone-sale row by its activity id. The detail page uses this
 * so it reads the exact row it navigated to (its own cache key) instead of
 * pulling a 500-row report window and finding it client-side — that window
 * flashed "phone sale not found, or you don't have access" right after creating
 * a sale (the new row fell outside the shared/stale report cache), which drove
 * operators to retry and create duplicates. Same access gate as the report;
 * returns the row, or null if not found / not visible to the caller.
 */
export async function getPhoneSaleById(
  activityId: UUID,
): Promise<PhoneSalesReportRow | null> {
  const { data, error } = await supabase.rpc("get_phone_sale_by_id", {
    p_activity_id: activityId,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as PhoneSalesReportRow[];
  return rows.length > 0 ? rows[0] : null;
}

// ---------------- get_counter_sales_report ----------------

export interface CounterSalesReportRow {
  voucher_id: UUID;
  voucher_code: string;
  contact_id: UUID | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  member_number: string | null;
  organization_id: UUID | null;
  credited_rep_position_id: UUID | null;
  voucher_amount: number | null;
  // Real Zuora subscription revenue (what the member paid). NULL when the
  // member has no synced Zuora data yet → UI shows "Not available". See
  // member_zuora_revenue() (migration 2026-06-23-006).
  subscription_revenue: number | null;
  voucher_status: string | null;
  voucher_created_at: string;
  total_count: number;
}

/**
 * Row-level counter-sales report (vouchers attached to org CTAs with
 * no event context). PII-gated: throws "Insufficient role" for roles
 * outside superadmin/admin/manager.
 */
export async function getCounterSalesReport(
  filter: SalesActivityReportFilter,
): Promise<{ rows: CounterSalesReportRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_counter_sales_report", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_rep_position_id: filter.repPositionId || null,
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as CounterSalesReportRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

// ---------------- get_counter_sales_aggregate ----------------

export interface CounterSalesAggregateRow {
  credited_rep_position_id: UUID | null;
  month: string; // ISO date (first of month)
  voucher_count: number;
  total_voucher_amount: number;
  // Sum of real Zuora subscription revenue across the group's sales; NULL when
  // none of the group's members have synced revenue. sales_with_revenue is how
  // many of voucher_count contributed a value (coverage indicator).
  total_subscription_revenue: number | null;
  sales_with_revenue: number;
  total_count: number;
}

/**
 * Aggregate counter sales by rep × month (per R7a-D1). "Unattributed"
 * appears as credited_rep_position_id IS NULL. Role-gated to
 * admin/superadmin/manager (no PII in output but data is
 * commission-adjacent; aligns with row report gating).
 */
export async function getCounterSalesAggregate(
  filter: SalesActivityReportFilter,
): Promise<{ rows: CounterSalesAggregateRow[]; total: number }> {
  const { data, error } = await supabase.rpc("get_counter_sales_aggregate", {
    p_org_ids: filter.orgIds && filter.orgIds.length > 0 ? filter.orgIds : null,
    p_date_from: filter.dateFrom || null,
    p_date_to: filter.dateTo || null,
    p_rep_position_id: filter.repPositionId || null,
    p_page: filter.page,
    p_limit: filter.limit,
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as CounterSalesAggregateRow[];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { rows, total };
}

// ---------------- listSalesPositions (rep filter dropdown) ----------------

export interface SalesPositionLite {
  id: UUID;
  title: string;
  assigned_name: string | null;
  is_active: boolean;
}

/**
 * Lightweight list of all sales_positions for use in report filters,
 * phone-sale rep dropdowns, etc. Bounded at 500 (per project rule —
 * see feedback_postgrest_default_to_bounded_select). Ordered by
 * assigned_name with nulls last.
 *
 * NOTE: `sales_positions` currently has wide-open SELECT RLS; R8 will
 * narrow this to MSR-scoped reads using `current_sales_position_ids()`.
 * When R8 tightens, callers may need to switch to an RPC that does
 * server-side scoping.
 */
export async function listSalesPositions(): Promise<SalesPositionLite[]> {
  const { data, error } = await supabase
    .from("sales_positions")
    .select("id, title, assigned_name, is_active")
    .order("assigned_name", { ascending: true, nullsFirst: false })
    .limit(500);
  if (error) {
    throw error;
  }
  return (data ?? []) as SalesPositionLite[];
}
